import { GRID_STRING_COL_DEF, GridApiPro, GridColDef, GridValidRowModel } from '@mui/x-data-grid-pro';
import { AppLocalStorageKeys } from '@/Infrastructure/Configuration/types';
import { RefObject } from 'react';

/**
 * Widths a dispatcher set by dragging a column separator, keyed by boardLayoutStorageKey the same way their
 * hidden columns are (see hiddenColumns.ts), so each board query keeps its own layout.
 *
 * Only widths that differ from the column definition's own are stored, so a dispatcher who drags a column back
 * to where it started leaves nothing behind.
 */
type ColumnWidthStore = Record<string, ColumnWidths | undefined>;

/** Widths keyed by field. Optional values because a field the dispatcher never resized has no entry. */
type ColumnWidths = Record<string, number | undefined>;

/**
 * What the grid itself falls back to for a column declaring no width of its own. GridColDef types both as
 * optional even though the definition MUI defaults from always sets them, so the literals pin what the types
 * leave open rather than letting `undefined` mean "no width Reset can restore".
 */
export const fallbackColumnWidth = GRID_STRING_COL_DEF.width ?? 100;
const fallbackColumnMinWidth = GRID_STRING_COL_DEF.minWidth ?? 50;

function readStore(): ColumnWidthStore {
  const stored = localStorage.getItem(AppLocalStorageKeys.DispatchColumnWidths);
  if (!stored) return {};

  try {
    const parsed: unknown = JSON.parse(stored);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};

    return Object.entries(parsed as Record<string, unknown>).reduce<ColumnWidthStore>((accumulator, [key, value]) => {
      if (typeof value !== 'object' || value === null || Array.isArray(value)) return accumulator;

      const widths = Object.entries(value as Record<string, unknown>).reduce<ColumnWidths>((fields, [field, width]) => {
        if (typeof width === 'number' && Number.isFinite(width) && width > 0) {
          fields[field] = width;
        }
        return fields;
      }, {});

      if (Object.keys(widths).length) {
        accumulator[key] = widths;
      }
      return accumulator;
    }, {});
  } catch {
    return {};
  }
}

function writeStore(store: ColumnWidthStore): void {
  localStorage.setItem(AppLocalStorageKeys.DispatchColumnWidths, JSON.stringify(store));
}

/**
 * Layers the dispatcher's widths onto the query's column definitions. Called only where the grid would otherwise
 * throw a resize away: MUI keeps a resized width in its own column state, and rebuilds that state from the
 * `columns` prop whenever its identity changes, so a stored width has to be part of the definitions it rebuilds
 * from. A stored field the query no longer carries is simply not matched.
 *
 * Re-clamps to minWidth on the way in so raising a column's minimum later cannot leave a dispatcher stuck with a
 * width narrower than the definition now allows.
 */
export function applyColumnWidths<T extends GridValidRowModel>(columns: GridColDef<T>[], key: string): GridColDef<T>[] {
  const widths = readStore()[key];
  if (!widths) return columns;

  return columns.map((column) => {
    const stored = widths[column.field];
    if (stored === undefined) return column;

    return { ...column, width: Math.max(stored, column.minWidth ?? fallbackColumnMinWidth) };
  });
}

/**
 * Records a resize, or clears the entry when the dispatcher lands back on the definition's own width.
 * MUI clamps to minWidth while the separator is being dragged, so `width` arrives already within bounds.
 */
export function persistColumnWidth(field: string, width: number, defaultWidth: number, key: string): void {
  const store = readStore();
  // JSON.stringify drops undefined values, so these clear the field, and the key when nothing deviates at all
  const widths: ColumnWidths = { ...store[key], [field]: width === defaultWidth ? undefined : width };
  const hasOverride = Object.values(widths).some((stored) => stored !== undefined);

  writeStore({ ...store, [key]: hasOverride ? widths : undefined });
}

/**
 * Returns every column to its definition width. Goes through the grid API rather than the `columns` prop so the
 * widths the dispatcher sees change without rebuilding the column definitions.
 *
 * Drops the stored entries up front rather than letting the resize events below clear them one by one, because
 * those events only reach columns the current query still carries — a field dropped from the query since it was
 * resized would otherwise outlive every reset and reappear if the query ever carried it again.
 */
export function resetColumnWidths(apiRef: RefObject<GridApiPro | null>, defaultWidths: Map<string, number>, key: string): void {
  const api = apiRef.current;
  if (!api) return;

  // JSON.stringify drops undefined values, so this removes the key outright
  writeStore({ ...readStore(), [key]: undefined });

  api.getAllColumns().forEach((column) => {
    const defaultWidth = defaultWidths.get(column.field);
    if (defaultWidth !== undefined && column.width !== defaultWidth) {
      api.setColumnWidth(column.field, defaultWidth);
    }
  });
}
